import * as React from 'react'
import { Link, useLoaderData } from 'react-router-dom'

import { withQueryClientContext } from '../../../../app/shared/components/QueryClientContext'
import { Pagination } from '../../../components/pagination'
import { getStepPagination } from '../../../hooks/use-table-pagination'
import type { ApiPaginatedResponse } from '../../../types'
import { CenteredAlert } from '../components/CenteredAlert'
import Layout from '../components/Layout'
import { FundHoldingsSection, ListCard } from '../components/ListCard'
import { ManagersAndFundsSearch } from '../components/ManagersAndFundsSearch'
import { ManagersFundsSortSelect } from '../components/ManagersFundsSortSelect'
import { InsiderView, ManagerFundItemData } from '../constants'
import { useFundsListData } from '../hooks/useFundsListData'
import { useInsiderTradingRouter } from '../hooks/useInsiderTradingRouter'

function Funds() {
  const initialData = useLoaderData() as ApiPaginatedResponse<ManagerFundItemData[]> | null
  const insiderTradingRouter = useInsiderTradingRouter<InsiderView.Funds>()
  const { page: currentPage, search, sort } = insiderTradingRouter.viewParams
  const fundsListData = useFundsListData({
    page: currentPage,
    initialData,
    search,
    sort,
    shouldResetScroll: true,
  })

  const { totalPages = 0 } = fundsListData.data ?? {}
  const stepNavigation = React.useMemo(
    () => getStepPagination({ pages: totalPages, currentPage, maxSidePreviewPages: 3 }),
    [totalPages, currentPage]
  )
  const items = fundsListData.data?.items ?? []

  return (
    <Layout
      middleContent={<ManagersAndFundsSearch searchIn="funds" />}
      rightContent={<ManagersFundsSortSelect view={InsiderView.Funds} sort={sort} />}
    >
      {items.length === 0 ? (
        <CenteredAlert>No funds match your search. Try using different keywords.</CenteredAlert>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-6 xl:grid-cols-3">
            {items.map((fundItem) => (
              <ListCard
                key={fundItem.filer.investorId}
                filer={fundItem.filer}
                latestSummaries={fundItem.latestSummaries}
                highlightHeaderLabel={search ?? undefined}
                href={insiderTradingRouter.getInsiderTradingLink({
                  view: InsiderView.FundDetails,
                  params: { investorId: fundItem.filer.investorId },
                })}
                bottomSection={<FundHoldingsSection topInvestments={fundItem.topInvestments} />}
              />
            ))}
          </div>

          <div className="flex w-full items-center justify-center overflow-hidden">
            <Pagination
              canGoBack={currentPage > 1}
              canGoForward={currentPage < totalPages}
              canGoToPage={(page) => page <= totalPages}
              visiblePages={stepNavigation}
              currentPage={currentPage}
              isLoading={fundsListData.isFetching}
              getPageItemButtonProps={(page) => ({
                as: Link,
                to: insiderTradingRouter.getInsiderTradingLink({
                  view: InsiderView.Funds,
                  params: { ...insiderTradingRouter.viewParams, page },
                }),
              })}
            />
          </div>
        </>
      )}
    </Layout>
  )
}

export default withQueryClientContext(Funds)
